import React, { useState, useEffect, useRef, useCallback } from 'react';
import './SectionNav.css';

const sections = [
  { id: 'intro', label: 'DWAG', sectionId: 'home-hero-section' },
  { id: 'quiz', label: 'Quiz', sectionId: 'home-quiz-section' },
  { id: 'flashcard', label: 'Flashcard', sectionId: 'home-flashcard-section' },
  { id: 'account', label: 'Compte', sectionId: 'home-account-section' },
  { id: 'pricing', label: 'Tarification', sectionId: 'home-pricing-section' },
  { id: 'faq', label: 'FAQ', sectionId: 'home-faq-section' },
  { id: 'cta', label: 'Commencer', sectionId: 'home-final-cta' },
];

const SectionNav = () => {
  const [activeSection, setActiveSection] = useState(sections[0].id);
  const [navbarHeight, setNavbarHeight] = useState(0);
  const lastHeightRef = useRef(0);
  const rafRef = useRef(null);

  // Fonction pour gérer le clic sur une section
  const handleSectionClick = (sectionId, section) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(section);
    } else {
      console.log(`Section "${sectionId}" non trouvée`);
    }
  };

  // Fonction pour mettre à jour la hauteur de la Navbar, memoized with useCallback
  const updateNavbarHeight = useCallback(() => {
    const navbar = document.querySelector('.navbar');
    if (navbar) {
      const height = navbar.offsetHeight;
      if (height !== lastHeightRef.current) {
        lastHeightRef.current = height;
        setNavbarHeight(height);
      }
    }

    // Continue checking until the height stabilizes
    rafRef.current = requestAnimationFrame(updateNavbarHeight);
  }, []); // Empty dependency array since setNavbarHeight and lastHeightRef are stable

  // Débouncer pour limiter les appels, memoized with useCallback
  const debounceUpdate = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }
    updateNavbarHeight();
  }, [updateNavbarHeight]); // Add updateNavbarHeight as a dependency

  useEffect(() => {
    // Initial calculation with MutationObserver
    const observer = new MutationObserver(() => {
      const navbar = document.querySelector('.navbar');
      if (navbar) {
        debounceUpdate();
        observer.disconnect();
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    // Écouter les événements de scroll et resize
    window.addEventListener('scroll', debounceUpdate);
    window.addEventListener('resize', debounceUpdate);

    return () => {
      observer.disconnect();
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
      window.removeEventListener('scroll', debounceUpdate);
      window.removeEventListener('resize', debounceUpdate);
    };
  }, [debounceUpdate]);

  // Détecter quelle section est visible à l’écran
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 100;
      sections.forEach((section) => {
        const element = document.getElementById(section.sectionId);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(section.id);
          }
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div
      className="section-nav-container"
      style={{
        top: `${navbarHeight}px`,
        height: `calc(100vh - ${navbarHeight}px)`,
      }}
    >
      <div className="section-nav">
        {sections.map((section, index) => (
          <div
            key={section.id}
            className={`section-item ${
              activeSection === section.id ? 'active' : ''
            }`}
            onClick={() => handleSectionClick(section.sectionId, section.id)}
          >
            <span className="section-number">{index + 1}</span>
            <span className="section-label">{section.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SectionNav;